import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { config } from '../../config';
import { useBoard } from '../../context/BoardContext';
import { useTheme } from '../../context/ThemeContext';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import {
  getPaletteForTheme,
  findPaletteIndexByColor,
  normalizeTheme,
} from '../../utils/listColorMapping';
import { showError } from '../../utils/snackbarHelpers';
import Close from '../icons/Close';
import './scss/ListColorPicker.scss';

const ListColorPicker = ({ listId, boardId }) => {
  const { boardState, setBoard } = useBoard();
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);

  const actualTheme = normalizeTheme(theme);
  const COLOR_PALETTE = getPaletteForTheme(actualTheme);

  const list = boardState?.actionLists?.find((l) => l._id === listId);
  const currentColorIndex = findPaletteIndexByColor(list?.color);

  const isTempList = typeof listId === 'string' && listId.startsWith('temp-');

  const handleSetListColor = async (colorData) => {
    const newColor = typeof colorData === 'string' ? colorData : colorData.bg;
    const newTextColor = typeof colorData === 'object' ? colorData.text : null;

    let prevColor = null;
    let prevTextColor = null;
    setBoard((prev) => {
      const lists = prev?.actionLists || [];
      const current = lists.find((l) => l._id === listId);
      prevColor = current?.color || null;
      prevTextColor = current?.textColor || null;
      return {
        ...prev,
        actionLists: lists.map((l) =>
          l._id === listId ? { ...l, color: newColor, textColor: newTextColor } : l
        ),
      };
    });

    dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));

    if (isTempList) return;

    try {
      await fetch(`${config.API_URI}/v1/actionLists/${listId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ boardId, color: newColor, textColor: newTextColor }),
      });
    } catch (error) {
      setBoard((prev) => ({
        ...prev,
        actionLists: (prev?.actionLists || []).map((l) =>
          l._id === listId ? { ...l, color: prevColor, textColor: prevTextColor } : l
        ),
      }));
      showError(dispatch, 'Error updating color: ' + error.message);
    }
  };

  const handleResetColor = () => handleSetListColor({ bg: null, text: null });

  const displayPalette = isExpanded ? COLOR_PALETTE : COLOR_PALETTE.slice(0, 10);

  return (
    <div className="list-color-picker">
      <div className="color-picker-grid">
        {displayPalette.map((colorData) => {
          const isSelected = currentColorIndex === colorData.index;
          return (
            <button
              key={colorData.index}
              className={`color-picker-swatch${isSelected ? ' selected' : ''}`}
              onClick={() => handleSetListColor(colorData)}
              style={
                colorData.bg && colorData.bg.startsWith('linear-gradient')
                  ? { background: colorData.bg }
                  : { backgroundColor: colorData.bg }
              }
            >
              {isSelected && (
                <span className="material-symbols-outlined">check</span>
              )}
            </button>
          );
        })}
      </div>
      {COLOR_PALETTE.length > 10 && (
        <button
          className="color-picker-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span className="material-symbols-outlined">
            {isExpanded ? 'expand_less' : 'expand_more'}
          </span>
          <span>{isExpanded ? 'Fewer colors' : 'More colors'}</span>
        </button>
      )}
      {list?.color && (
        <button className="color-picker-remove" onClick={handleResetColor}>
          <Close size={14} />
          <span>Remove color</span>
        </button>
      )}
    </div>
  );
};

export default ListColorPicker;
